import { useMemo, useState } from 'react';
import React from 'react';
import styled from 'styled-components';

import DeleteAdmissionModal from 'components/modals/DeleteAdmissionModal';
import EditAdmissionPatientModal from 'components/modals/EditAdmissionPatientModal';
import DividerSeries from 'components/shared/DividerSeries';
import Menu from 'components/shared/menu';
import { useProfile } from 'context/profile';
import { PERMISSIONS } from 'models/Profile';
import Admission from 'models/reviews/Admission';
import colors from 'styles/theme/colors';
import PencilIcon from 'svg/PencilIcon';
import ThreeDotMenu from 'svg/ThreeDotMenu';
import TrashIcon from 'svg/TrashIcon';

interface Props {
  admission: Admission;
}

export default function AdmissionsActionsMenu({ admission }: Props) {
  const { profile } = useProfile();
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);

  const canEdit = profile.has(PERMISSIONS.reviewAdmissionEdit) && admission.pending && !admission.isProcessing;
  const canDelete = profile.has(PERMISSIONS.reviewAdmissionDelete);

  const actions: {
    icon: React.ReactNode;
    label: string;
    onClick: () => void;
    show?: boolean;
    color?: string;
  }[][] = useMemo(() => {
    return [
      [
        {
          icon: <PencilIcon color={'transparent'} stroke={colors.black} strokeWidth={1.5} />,
          label: 'Edit Patient Information',
          onClick: () => setShowEditModal(true),
          show: canEdit,
        },
      ],
      [
        {
          icon: <TrashIcon width={16} height={16} />,
          label: 'Delete Referral',
          onClick: () => setShowDeleteModal(true),
          color: colors.accentRed,
          show: canDelete,
        },
      ],
    ];
  }, [canEdit, canDelete]);

  const showMenu = actions.some((section) => section.some((action) => action.show));

  if (!showMenu) return null;

  return (
    <>
      <Menu $size='large'>
        <Menu.Trigger>
          <MenuButton data-cy='admissionsActionsMenuButton' aria-label='Admission actions'>
            <ThreeDotMenu />
          </MenuButton>
        </Menu.Trigger>
        <Menu.Content position='right' style={{ marginTop: 4 }}>
          <DividerSeries divider={<Menu.Divider />}>
            {actions.map((section, index) => (
              <React.Fragment key={index}>
                {section
                  .filter((action) => action.show)
                  .map((action, i) => (
                    <StyledMenuItem
                      data-cy='admissions-action-menu-item'
                      style={{ color: action.color }}
                      key={i}
                      onClick={action.onClick}>
                      {action.icon}
                      {action.label}
                    </StyledMenuItem>
                  ))}
              </React.Fragment>
            ))}
          </DividerSeries>
        </Menu.Content>
      </Menu>

      {showEditModal && <EditAdmissionPatientModal admission={admission} onCancel={() => setShowEditModal(false)} />}

      {showDeleteModal && <DeleteAdmissionModal admission={admission} onCancel={() => setShowDeleteModal(false)} />}
    </>
  );
}

const StyledMenuItem = styled(Menu.Item)`
  gap: 12px;
`;

const MenuButton = styled.button`
  all: unset;
  cursor: pointer;
  height: 16px;
  padding: 0 2px;
  display: flex;
  align-items: center;
  justify-content: center;
`;
